import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export const Terms = () => (
  <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-black text-white">
    <div className="container mx-auto px-4 py-12">
      <Link
        to="/"
        className="inline-flex items-center text-gray-400 hover:text-white mb-8"
      >
        <ArrowLeft className="mr-2" /> Back to Home
      </Link>

      <div className="glass-card p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">Terms and Conditions</h1>
        <p className="text-gray-400 mb-8">Effective Date: April 6, 2026</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              1. Acceptance of Terms
            </h2>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding
              agreement between you ("User") and Agent FOMO ("Company," "we,"
              "us," or "our"). By accessing or using the Agent FOMO platform
              ("Service"), you acknowledge that you have read, understood, and
              agree to be bound by these Terms. If you do not agree to these
              Terms, you must discontinue use of the Service immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              2. Description of Service
            </h2>
            <p>
              Agent FOMO provides an AI-powered event discovery and notification
              service, delivered primarily through WhatsApp messaging. The
              Service enables users to search for events, receive personalized
              recommendations, and discover experiences in their area. We
              reserve the right to modify, suspend, or discontinue any aspect of
              the Service at our sole discretion and without prior notice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              3. Event Data and Accuracy
            </h2>
            <p className="mb-4">
              Event information displayed through the Service is gathered from a
              variety of public sources, including social media, venue websites,
              ticketing platforms, and user submissions. While we make
              reasonable efforts to ensure accuracy and to correct errors when
              they are brought to our attention, we cannot guarantee that event
              details — including dates, times, venues, prices, lineups, or
              availability — are accurate, complete, or up to date. Event
              information may change without notice.
            </p>
            <p>
              You are encouraged to verify event details directly with the event
              organizer or venue before making plans or purchases. Agent FOMO
              shall not be held liable for any inaccuracies in event information
              or for any loss, expense, or inconvenience resulting from reliance
              on such information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              4. AI-Powered Service
            </h2>
            <p>
              The Service relies on artificial intelligence to process queries,
              generate event recommendations, and interact with users.
              AI-generated responses may be inaccurate, incomplete, or
              misleading. You should not rely solely on the Service for making
              decisions and should independently verify any information that is
              important to you. Agent FOMO does not guarantee the correctness,
              relevance, or completeness of any AI-generated output.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              5. User-Submitted Content
            </h2>
            <p className="mb-4">
              Users may submit event information and other content through the
              Service. By submitting content, you grant Agent FOMO a
              non-exclusive, royalty-free, worldwide license to use, display,
              modify, and distribute that content in connection with the
              Service. You represent that you have the right to submit such
              content and that it does not infringe on the rights of any third
              party.
            </p>
            <p>
              We reserve the right to edit, remove, or decline to display any
              user-submitted content at our sole discretion and without notice.
              You are solely responsible for the accuracy of content you submit.
              Agent FOMO is not liable for any errors, omissions, or
              consequences arising from user-submitted content.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              6. Eligibility
            </h2>
            <p>
              You must be at least 18 years of age to use the Service. By using
              the Service, you represent and warrant that you meet this age
              requirement and have the legal capacity to enter into these Terms.
              If you are using the Service on behalf of an organization, you
              represent that you have the authority to bind that organization to
              these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              7. User Obligations
            </h2>
            <p className="mb-4">You agree to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                Provide accurate, current, and complete information when
                interacting with the Service.
              </li>
              <li>
                Use the Service solely for lawful purposes and in compliance
                with all applicable laws and regulations.
              </li>
              <li>
                Refrain from attempting to gain unauthorized access to the
                Service's systems, data, or networks.
              </li>
              <li>
                Not use the Service for the transmission of unsolicited
                communications, spam, or any form of harassment.
              </li>
              <li>
                Not engage in any activity that disrupts or interferes with the
                proper functioning of the Service.
              </li>
            </ul>
            <p>
              We reserve the right to suspend or terminate your access to the
              Service if you breach any of these obligations, without prior
              notice and without liability.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              8. Payments and Refunds
            </h2>
            <p className="mb-4">
              Certain features of the Service, such as event promotion, may
              require payment. All payments are processed through third-party
              payment providers. By making a purchase, you agree to the pricing
              and payment terms presented at the time of the transaction.
            </p>
            <p>
              All purchases are final and non-refundable unless otherwise
              required by applicable law. Agent FOMO does not store your payment
              card details. For payment disputes, please contact us via
              WhatsApp.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              9. Intellectual Property
            </h2>
            <p>
              All content, software, trademarks, logos, designs, and other
              materials available through the Service are the exclusive property
              of Agent FOMO or its licensors and are protected by applicable
              intellectual property laws. No license or right is granted to you
              by implication or otherwise under any intellectual property rights
              owned or controlled by Agent FOMO. You may not reproduce,
              distribute, modify, create derivative works from, publicly
              display, or otherwise exploit any of our proprietary materials
              without our express written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              10. Third-Party Content and Services
            </h2>
            <p>
              The Service may contain links to or information about third-party
              events, venues, and services. Agent FOMO does not endorse,
              warrant, or assume responsibility for the accuracy, completeness,
              or quality of any third-party content. Your interactions with
              third-party event organizers, venues, or service providers are
              solely between you and such third parties. Agent FOMO shall not be
              liable for any loss or damage arising from such interactions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              11. Communications and Proactive Messaging
            </h2>
            <p className="mb-4">
              By using the Service, you consent to receive messages from Agent
              FOMO via WhatsApp, including event recommendations, subscription
              notifications, and periodic re-engagement messages. These messages
              are part of the Service and are sent to enhance your experience.
            </p>
            <p>
              You may opt out of proactive messages at any time by messaging the
              bot and requesting to unsubscribe. Opting out of proactive
              messages does not affect your ability to use the Service on
              demand.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              12. Disclaimer of Warranties
            </h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
              WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR
              STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF HARMFUL COMPONENTS.
              WE MAKE NO REPRESENTATIONS REGARDING THE ACCURACY OR RELIABILITY
              OF ANY INFORMATION PROVIDED THROUGH THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              13. Limitation of Liability
            </h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, AGENT FOMO, ITS
              OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
              DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR
              GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR
              INABILITY TO USE THE SERVICE, REGARDLESS OF THE THEORY OF
              LIABILITY AND EVEN IF AGENT FOMO HAS BEEN ADVISED OF THE
              POSSIBILITY OF SUCH DAMAGES.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              14. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless Agent FOMO and
              its affiliates, officers, directors, employees, and agents from
              and against any and all claims, liabilities, damages, losses,
              costs, and expenses (including reasonable attorneys' fees) arising
              out of or related to your use of the Service, your violation of
              these Terms, or your violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              15. Modifications to Terms
            </h2>
            <p>
              We reserve the right to amend or update these Terms at any time at
              our sole discretion. Material changes will be communicated through
              the Service or by other reasonable means. Your continued use of
              the Service following the posting of revised Terms constitutes
              your acceptance of such changes. It is your responsibility to
              review these Terms periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              16. Governing Law and Jurisdiction
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of the State of Israel, without regard to its conflict of
              law provisions. Any dispute arising out of or relating to these
              Terms or the Service shall be subject to the exclusive
              jurisdiction of the competent courts located in Tel Aviv-Jaffa,
              Israel.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              17. Severability
            </h2>
            <p>
              If any provision of these Terms is found to be invalid or
              unenforceable by a court of competent jurisdiction, the remaining
              provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4 text-white">
              18. Contact Information
            </h2>
            <p>
              For any questions, concerns, or requests regarding these Terms,
              please contact us via WhatsApp at{" "}
              <a
                href="https://wa.me/972504244206"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:underline"
              >
                +972-50-424-4206
              </a>
              .
            </p>
          </section>

          <div className="pt-8 flex justify-center">
            <a
              href="https://wa.me/972504244206"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-all hover:scale-105"
            >
              Back to Chat
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
);
